/**
 * TenantTabPanels — obszar treści powłoki tenanta z kartami (wzorzec SellCall `TabPanels`).
 *
 * Renderuje WSZYSTKIE otwarte karty naraz, każdą w `absolute inset-0`, widoczna tylko aktywna
 * (`display`). Dzięki temu karty w tle żyją — zachowany stan, scroll, niezapisany formularz.
 * Lazy-mount: drzewo karty montuje się dopiero przy pierwszej aktywacji.
 *
 * Każda karta ma WŁASNY `<Routes location={tabPath}>` — niezależny od globalnego URL-a, więc
 * `useParams`/`useSearchParams` w stronach rozwiązują się względem ścieżki danej karty.
 *
 * Tabela tras = post-login trasy tenanta (przeniesione z `App.tsx`). Pre-login (`/login`…)
 * oraz pełnoekranowy builder procesu (`/processes/builder/:id`) żyją poza powłoką (w `App.tsx`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { ModuleAreaGuard } from '@/components/ModuleAreaGuard';
import { ModuleGuard } from '@/components/ModuleGuard';
import { AreaMapPage } from '@/components/Sidebar';
import { useTabs } from '@/contexts/TabContext';
import { CalendarPage } from '@/pages/CalendarPage';
import { CommercialDocDetail } from '@/pages/CommercialDocDetail';
import { CommercialDocs } from '@/pages/CommercialDocs';
import { Companies } from '@/pages/Companies';
import { CompanyDetail } from '@/pages/CompanyDetail';
import { ContractDetail } from '@/pages/ContractDetail';
import { ContractorContactDetail } from '@/pages/ContractorContactDetail';
import { ContractorContacts } from '@/pages/ContractorContacts';
import { ContractorDetail } from '@/pages/ContractorDetail';
import { Contractors } from '@/pages/Contractors';
import { Contracts } from '@/pages/Contracts';
import { DashboardDefaultEditor } from '@/pages/DashboardDefaultEditor';
import { EmployeeDetail } from '@/pages/EmployeeDetail';
import { Employees } from '@/pages/Employees';
import { HmConnection } from '@/pages/handel-magazyn/HmConnection';
import { HmContractorDetail } from '@/pages/handel-magazyn/HmContractorDetail';
import { HmContractorForm } from '@/pages/handel-magazyn/HmContractorForm';
import { HmContractors } from '@/pages/handel-magazyn/HmContractors';
import { HmFiscalStations } from '@/pages/handel-magazyn/HmFiscalStations';
import { HmOrderDetail } from '@/pages/handel-magazyn/HmOrderDetail';
import { HmOrders } from '@/pages/handel-magazyn/HmOrders';
import { HmPaymentDetail } from '@/pages/handel-magazyn/HmPaymentDetail';
import { HmPaymentNew } from '@/pages/handel-magazyn/HmPaymentNew';
import { HmPayments } from '@/pages/handel-magazyn/HmPayments';
import { HmProductDetail } from '@/pages/handel-magazyn/HmProductDetail';
import { HmProductNew } from '@/pages/handel-magazyn/HmProductNew';
import { HmProductReport } from '@/pages/handel-magazyn/HmProductReport';
import { HmProducts } from '@/pages/handel-magazyn/HmProducts';
import { HmSaleDetail } from '@/pages/handel-magazyn/HmSaleDetail';
import { HmSaleNew } from '@/pages/handel-magazyn/HmSaleNew';
import { HmSales } from '@/pages/handel-magazyn/HmSales';
import { HmSalesReport } from '@/pages/handel-magazyn/HmSalesReport';
import { HmWarehouse } from '@/pages/handel-magazyn/HmWarehouse';
import { HmWarehouseDetail } from '@/pages/handel-magazyn/HmWarehouseDetail';
import { HmWarehouseForm } from '@/pages/handel-magazyn/HmWarehouseForm';
import { HmWarehouseReport } from '@/pages/handel-magazyn/HmWarehouseReport';
import { HrDashboard } from '@/pages/hr/HrDashboard';
import { InternalDocDetail } from '@/pages/InternalDocDetail';
import { InternalDocs } from '@/pages/InternalDocs';
import { KsefInvoices } from '@/pages/KsefInvoices';
import { LetterDetail } from '@/pages/LetterDetail';
import { Letters } from '@/pages/Letters';
import { MenuBuilderPage } from '@/pages/menu/MenuBuilderPage';
import { CategorySettingsPage } from '@/pages/modules/CategorySettingsPage';
import { ModuleBuilderPage } from '@/pages/modules/ModuleBuilderPage';
import { ModuleRecordsPage } from '@/pages/modules/ModuleRecordsPage';
import { ModuleSettingsPage } from '@/pages/modules/ModuleSettingsPage';
import { ModulesPage } from '@/pages/modules/ModulesPage';
import { RecordFormPage } from '@/pages/modules/RecordFormPage';
import { MyProfile } from '@/pages/MyProfile';
import { MyTenants } from '@/pages/MyTenants';
import { OrgChart } from '@/pages/OrgChart';
import { ApprovalsPage } from '@/pages/processes/ApprovalsPage';
import { ProcessesListPage } from '@/pages/processes/ProcessesListPage';
import { ProcessTemplatesGalleryPage } from '@/pages/processes/ProcessTemplatesGalleryPage';
import { ProductDetail } from '@/pages/ProductDetail';
import { Products } from '@/pages/Products';
import { PurchaseInvoiceDetail } from '@/pages/PurchaseInvoiceDetail';
import { PurchaseInvoices } from '@/pages/PurchaseInvoices';
import { SaleInvoiceDetail } from '@/pages/SaleInvoiceDetail';
import { SaleInvoices } from '@/pages/SaleInvoices';
import { ScheduleGrid } from '@/pages/ScheduleGrid';
import { Settings } from '@/pages/Settings';
import { SpecialDocDetail } from '@/pages/SpecialDocDetail';
import { SpecialDocs } from '@/pages/SpecialDocs';
import { SystemModuleBuilderPage } from '@/pages/system-modules/SystemModuleBuilderPage';
import { SystemModuleListBuilderPage } from '@/pages/system-modules/SystemModuleListBuilderPage';
import { SystemModuleSettingsPage } from '@/pages/system-modules/SystemModuleSettingsPage';
import { SystemModulesPage } from '@/pages/system-modules/SystemModulesPage';
import { Tenant2FASetup } from '@/pages/Tenant2FASetup';
import { TenantDashboard } from '@/pages/TenantDashboard';
import { UserDetail } from '@/pages/UserDetail';
import { Users } from '@/pages/Users';

/**
 * `/employees/new` i `/employees/:id` renderują ten sam komponent, więc React
 * reużywa instancję przy przejściu (np. „zapisany pracownik → dodaj kolejnego")
 * i formularz zostaje ze starymi danymi. `key` zależny od id (albo 'new') wymusza
 * świeży montaż formularza przy każdej zmianie rekordu — czyści WSZYSTKIE pola
 * (podstawowe, dodatkowe, tabele) bez ręcznego resetu.
 */
function EmployeeDetailKeyed() {
  const { id } = useParams();
  return <EmployeeDetail key={id ?? 'new'} />;
}

/** Parsuje string ścieżki na obiekt Location dla `<Routes location>`. */
function parseLocation(path: string) {
  const [pathname, search] = path.split('?');
  return {
    pathname: pathname || '/',
    search: search ? `?${search}` : '',
    hash: '',
    state: null,
    key: 'default',
  };
}

function TabNotFound() {
  const { t } = useTranslation();
  return (
    <div className="flex h-64 items-center justify-center text-text-tertiary">
      <p className="text-lg">{t('tabs.notFound')}</p>
    </div>
  );
}

/** Tabela tras tenanta — renderowana per karta z jej własnym `location`. */
function TenantRoutes({ location }: { location: ReturnType<typeof parseLocation> }) {
  return (
    <Routes location={location}>
      <Route path="/dashboard" element={<TenantDashboard />} />
      {/* Kalendarz — placeholder (decyzja Eugenii 2026-09-17); właściwy kalendarz = osobny temat. */}
      <Route path="/calendar" element={<CalendarPage />} />
      {/* Mapa obszarów — pełny spis kartotek jako zwykły widok (nie modal). Decyzja Eugenii 2026-09-17. */}
      <Route path="/area-map" element={<AreaMapPage />} />
      {/* Faza 23 Etap 2 — edytor domyślnego pulpitu tenanta (admin-only, wejście z Ustawień). */}
      <Route path="/dashboard/default" element={<DashboardDefaultEditor />} />
      <Route path="/tenants" element={<MyTenants />} />
      <Route path="/companies" element={<Companies />} />
      <Route path="/companies/new" element={<CompanyDetail />} />
      <Route path="/companies/:id" element={<CompanyDetail />} />
      <Route path="/contractors" element={<Contractors />} />
      <Route path="/contractors/new" element={<ContractorDetail />} />
      <Route path="/contractors/:id" element={<ContractorDetail />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/:id" element={<ProductDetail />} />
      <Route path="/purchase-invoices" element={<PurchaseInvoices />} />
      <Route path="/purchase-invoices/new" element={<PurchaseInvoiceDetail />} />
      <Route path="/purchase-invoices/:id" element={<PurchaseInvoiceDetail />} />
      <Route path="/sale-invoices" element={<SaleInvoices />} />
      <Route path="/sale-invoices/new" element={<SaleInvoiceDetail />} />
      <Route path="/sale-invoices/:id" element={<SaleInvoiceDetail />} />
      <Route path="/ksef" element={<KsefInvoices />} />
      {/* Handel i Magazyn — okno na Symfonię Handel (dane czytane na żywo, bez naszych tabel).
          Pozycje menu modułu to `custom_link` wskazujące dokładnie te adresy. */}
      <Route path="/handel-magazyn/sprzedaz" element={<HmSales />} />
      {/* „nowy" PRZED trasą z parametrem — inaczej wpadłoby w `:id` jako identyfikator. */}
      <Route path="/handel-magazyn/sprzedaz/nowy" element={<HmSaleNew />} />
      {/* Zestawienia też PRZED `:id` — `zestawienia` nie jest identyfikatorem dokumentu. */}
      <Route path="/handel-magazyn/sprzedaz/zestawienia/:report" element={<HmSalesReport />} />
      <Route path="/handel-magazyn/sprzedaz/:id" element={<HmSaleDetail />} />
      <Route path="/handel-magazyn/magazyn" element={<HmWarehouse />} />
      {/* „nowy" i „edycja" PRZED trasą z parametrem — inaczej wpadłyby w `:id`. */}
      <Route path="/handel-magazyn/magazyn/nowy" element={<HmWarehouseForm mode="create" />} />
      {/* Zestawienia PRZED `:id` — `zestawienia` nie jest identyfikatorem dokumentu. */}
      <Route path="/handel-magazyn/magazyn/zestawienia/:report" element={<HmWarehouseReport />} />
      <Route path="/handel-magazyn/magazyn/:id/edycja" element={<HmWarehouseForm mode="edit" />} />
      <Route path="/handel-magazyn/magazyn/:id" element={<HmWarehouseDetail />} />
      <Route path="/handel-magazyn/zamowienia" element={<HmOrders />} />
      <Route path="/handel-magazyn/zamowienia/:id" element={<HmOrderDetail />} />
      <Route path="/handel-magazyn/kasa-bank" element={<HmPayments />} />
      {/* „nowy" PRZED trasą z parametrem — inaczej wpadłoby w `:id`. */}
      <Route path="/handel-magazyn/kasa-bank/nowy" element={<HmPaymentNew />} />
      <Route path="/handel-magazyn/kasa-bank/:id" element={<HmPaymentDetail />} />
      <Route path="/handel-magazyn/towary" element={<HmProducts />} />
      {/* Zestawienia PRZED `:id` — `zestawienia` nie jest identyfikatorem towaru. */}
      {/* „nowy" PRZED trasą z parametrem — inaczej wpadłoby w `:id`. */}
      <Route path="/handel-magazyn/towary/nowy" element={<HmProductNew />} />
      <Route path="/handel-magazyn/towary/zestawienia/:report" element={<HmProductReport />} />
      <Route path="/handel-magazyn/towary/:id" element={<HmProductDetail />} />
      <Route path="/handel-magazyn/kontrahenci" element={<HmContractors />} />
      {/* „nowy" PRZED trasą z parametrem — inaczej wpadłoby w `:id`. */}
      <Route path="/handel-magazyn/kontrahenci/nowy" element={<HmContractorForm mode="create" />} />
      <Route
        path="/handel-magazyn/kontrahenci/:id/edycja"
        element={<HmContractorForm mode="edit" />}
      />
      <Route path="/handel-magazyn/kontrahenci/:id" element={<HmContractorDetail />} />
      <Route path="/handel-magazyn/polaczenie" element={<HmConnection />} />
      {/* Stanowiska z drukarką fiskalną — konfiguracja usługi drukującej u klienta. */}
      <Route path="/handel-magazyn/drukarki-fiskalne" element={<HmFiscalStations />} />
      <Route path="/contracts" element={<Contracts />} />
      <Route path="/contracts/new" element={<ContractDetail />} />
      <Route path="/contracts/:id" element={<ContractDetail />} />
      <Route path="/letters" element={<Letters />} />
      <Route path="/letters/new" element={<LetterDetail />} />
      <Route path="/letters/:id" element={<LetterDetail />} />
      <Route path="/commercial-docs" element={<CommercialDocs />} />
      <Route path="/commercial-docs/new" element={<CommercialDocDetail />} />
      <Route path="/commercial-docs/:id" element={<CommercialDocDetail />} />
      <Route path="/internal-docs" element={<InternalDocs />} />
      <Route path="/internal-docs/new" element={<InternalDocDetail />} />
      <Route path="/internal-docs/:id" element={<InternalDocDetail />} />
      <Route path="/special-docs" element={<SpecialDocs />} />
      <Route path="/special-docs/new" element={<SpecialDocDetail />} />
      <Route path="/special-docs/:id" element={<SpecialDocDetail />} />
      <Route path="/settings" element={<Settings />} />
      {/* Każda sekcja Ustawień jako osobna karta powłoki (własny basePath = keep-alive). */}
      <Route path="/settings/:section" element={<Settings />} />
      <Route path="/modules" element={<ModulesPage />} />
      <Route path="/system-modules" element={<SystemModulesPage />} />
      <Route path="/system-modules/:slug/settings" element={<SystemModuleSettingsPage />} />
      <Route path="/system-modules/:slug/builder" element={<SystemModuleBuilderPage />} />
      <Route path="/system-modules/:slug/kreator-listy" element={<SystemModuleListBuilderPage />} />
      <Route
        path="/modules/:slug/kreator-listy"
        element={<SystemModuleListBuilderPage scope="module" />}
      />
      <Route path="/modules/:id/builder" element={<ModuleBuilderPage />} />
      <Route path="/modules/:id/categories" element={<CategorySettingsPage />} />
      <Route path="/modules/:id/settings" element={<ModuleSettingsPage />} />
      <Route path="/modules/:slug/records/new" element={<RecordFormPage />} />
      <Route path="/modules/:slug/:recordId" element={<RecordFormPage />} />
      <Route path="/modules/:slug" element={<ModuleRecordsPage />} />
      <Route path="/profile" element={<MyProfile />} />
      <Route path="/profile/2fa/setup" element={<Tenant2FASetup />} />
      <Route path="/users" element={<Users />} />
      <Route path="/users/:id" element={<UserDetail />} />
      <Route path="/contractor-contacts" element={<ContractorContacts />} />
      <Route path="/contractor-contacts/new" element={<ContractorContactDetail />} />
      <Route path="/contractor-contacts/:id" element={<ContractorContactDetail />} />
      <Route path="/employees" element={<Employees />} />
      <Route path="/employees/new" element={<EmployeeDetailKeyed />} />
      <Route path="/employees/:id" element={<EmployeeDetailKeyed />} />
      <Route path="/org-chart" element={<OrgChart />} />
      {/* Pulpit HR — aktywny przy DOWOLNYM module z obszaru `hr` (decyzja 2026-10-05). */}
      <Route
        path="/hr"
        element={
          <ModuleAreaGuard areaCode="hr" areaLabelKey="hr.nav.section">
            <HrDashboard />
          </ModuleAreaGuard>
        }
      />
      {/* Grafik zmian — rekordy modułu Kreatora `grafiki_zmiany` (moduł „Grafiki i RCP"). */}
      <Route
        path="/grafik"
        element={
          <ModuleGuard moduleCode="grafiki">
            <ScheduleGrid />
          </ModuleGuard>
        }
      />
      <Route path="/menu-builder" element={<MenuBuilderPage />} />
      <Route path="/processes" element={<ProcessesListPage />} />
      <Route path="/processes/decisions/:tab" element={<ApprovalsPage />} />
      <Route
        path="/processes/decisions"
        element={<Navigate to="/processes/decisions/pending" replace />}
      />
      <Route path="/processes/templates" element={<ProcessTemplatesGalleryPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<TabNotFound />} />
    </Routes>
  );
}

export function TenantTabPanels() {
  const { tabs, activeTabId } = useTabs();

  // Lazy-mount: drzewo karty montuje się dopiero przy pierwszej aktywacji.
  const [mountedTabIds, setMountedTabIds] = useState<Set<string>>(() =>
    activeTabId ? new Set([activeTabId]) : new Set(),
  );

  useEffect(() => {
    if (activeTabId && !mountedTabIds.has(activeTabId)) {
      setMountedTabIds((prev) => new Set([...prev, activeTabId]));
    }
  }, [activeTabId, mountedTabIds]);

  // Sprzątanie: usuń zamknięte karty z zamontowanych.
  useEffect(() => {
    const ids = new Set(tabs.map((t) => t.id));
    setMountedTabIds((prev) => {
      const updated = new Set([...prev].filter((id) => ids.has(id)));
      return updated.size !== prev.size ? updated : prev;
    });
  }, [tabs]);

  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="tab-panels">
      {tabs.map((tab) => {
        if (!mountedTabIds.has(tab.id)) return null;
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            className="absolute inset-0 overflow-auto"
            style={{ display: isActive ? 'block' : 'none' }}
            data-testid={`tab-panel-${tab.basePath}`}
          >
            <TenantRoutes location={parseLocation(tab.path)} />
          </div>
        );
      })}
    </div>
  );
}
